import { useCallback } from "react"
import PropTypes from "prop-types"
import { Paper } from "@mui/material"
import { useSyncGraphPreferences } from "connected-components/dashboard/hooks/use-sync-graph-preferences"
import { useTemplates, useTemplateMetadata, useRetryTemplatesList, useRetryTemplateMetadata } from "resources/templates"
import { useContentSetsMetadata } from "resources/content-sets"
import { OK, ERROR } from "helpers/data-statuses"
import GraphErrorFallback from "renderers/multi-line-graph/components/graph-error-fallback"
import DuplicateViewButton from "connected-components/asset-view/components/duplicate-view-button"
import { beginDuplicateBuiltInView } from "connected-components/asset-view/helpers/duplicate-view-flow"
import { useDisplayedMetricIds } from "connected-components/asset-view/hooks/use-displayed-metric-ids"
import { useIsDashboardCustomGraphFeatureEnabled, useAreEnabledFeaturesResolved, useHaveEnabledFeaturesFailed } from "resources/account"
import { DEFAULT_GRAPH_STATE } from "connected-components/dashboard/store/graph-slice"
import { useGraphData, isContentSetTemplateId, resolveIsBuiltInGraphTemplate, useGraphTemplateResolution } from "./hooks/use-graph-data"
import { useGraphDuplicateFlow } from "./hooks/use-graph-duplicate-flow"
import performanceTemplate from "./templates/performance"
import riskTemplate from "./templates/risk"
import exposureTemplate from "./templates/exposure"
import compositionTemplate from "./templates/composition"
import betaTemplate from "./templates/beta"
import contentSetTemplate from "./templates/content-set"
import customTemplate from "./templates/custom"
import { historical as factorSensitivityHistorical, predicted as factorSensitivityPredicted } from "./templates/factor-sensitivity"
import {
  GRAPH_CATEGORY_DEFAULTS,
  GRAPH_CUSTOM_TEMPLATE_TYPE,
  gateCustomTemplatesResult,
  gateCustomTemplatesStatus,
} from "./templates/template-chart-config"
import { useGraphTemplateChart } from "./hooks/use-graph-template-chart"
import { resolveFamilyContextFromBuiltInViewId, isCategoryEligibleForDuplicate } from "./templates/custom/family"
import { MODEL_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import { INSTRUMENTS_WITH_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import { CONTENT_SET_TEMPLATE_PREFIX, GRAPH_TEMPLATE_LABELS } from "./components/template-selector"

const NAMED_TEMPLATES = {
  performance: performanceTemplate,
  risk: riskTemplate,
  exposure: exposureTemplate,
  composition: compositionTemplate,
  beta: betaTemplate,
  "factor-sensitivity-historical": factorSensitivityHistorical,
  "factor-sensitivity-predicted": factorSensitivityPredicted,
}

function resolveTemplate(selectedTemplate, isCustomTemplate, isCustomGraphFeatureEnabled) {
  if (isCustomTemplate) {
    return isCustomGraphFeatureEnabled ? customTemplate : NAMED_TEMPLATES[DEFAULT_GRAPH_STATE.selectedTemplate]
  }
  if (isContentSetTemplateId(selectedTemplate)) return contentSetTemplate
  return NAMED_TEMPLATES[selectedTemplate]
}

export default function GraphWrapper(props) {
  const { initialSelectedTemplate, initialDateOverride, instrumentsWithMetadata, modelMetadata } = props
  const {
    selectedTemplate,
    setSelectedTemplate,
    isGraphInitialized,
    duplicateFromTemplateId,
    duplicateReturnViewId,
  } = useGraphData(initialSelectedTemplate, initialDateOverride)
  useSyncGraphPreferences()

  const isCustomGraphFeatureEnabled = useIsDashboardCustomGraphFeatureEnabled()
  const areEnabledFeaturesResolved = useAreEnabledFeaturesResolved()
  const haveEnabledFeaturesFailed = useHaveEnabledFeaturesFailed()
  const [rawCustomTemplates, rawCustomTemplatesStatus] = useTemplates(GRAPH_CUSTOM_TEMPLATE_TYPE)
  const customTemplates = gateCustomTemplatesResult(rawCustomTemplates, isCustomGraphFeatureEnabled)
  const customTemplatesStatus = gateCustomTemplatesStatus(
    rawCustomTemplatesStatus,
    isCustomGraphFeatureEnabled,
    areEnabledFeaturesResolved,
    haveEnabledFeaturesFailed,
  )
  const isCustomGraphFeatureUnresolved = !isCustomGraphFeatureEnabled && !areEnabledFeaturesResolved && !haveEnabledFeaturesFailed
  const [contentSetsMetadata] = useContentSetsMetadata()

  const isBuiltInTemplate = resolveIsBuiltInGraphTemplate(selectedTemplate, Boolean(NAMED_TEMPLATES[selectedTemplate]))
  const templateId = selectedTemplate === "custom" || isBuiltInTemplate || !isCustomGraphFeatureEnabled ? null : selectedTemplate
  const [templateMetadata, templateMetadataStatus] = useTemplateMetadata(templateId)
  const retryTemplatesList = useRetryTemplatesList()
  const retryTemplateMetadata = useRetryTemplateMetadata(templateId)

  const {
    isCustomTemplate,
    isOrphanedCustomSentinel,
    isStaleCustomTemplateId,
    isTemplateListLoadFailure,
    isSelectedTemplateLoadFailure,
  } = useGraphTemplateResolution({
    selectedTemplate,
    setSelectedTemplate,
    duplicateFromTemplateId,
    duplicateReturnViewId,
    isBuiltInTemplate,
    customTemplates,
    customTemplatesStatus,
    templateId,
    templateMetadataStatus,
  })

  const { sourcedMetricIdsRef, handleDisplayedMetricIdsChange } = useDisplayedMetricIds(selectedTemplate)
  const duplicateFlow = useGraphDuplicateFlow()
  const { selectedCategory } = useGraphTemplateChart(selectedTemplate, GRAPH_CATEGORY_DEFAULTS)

  const handleDuplicateView = useCallback(() => {
    const contentSet = contentSetsMetadata?.find((contentSetItem) => `${CONTENT_SET_TEMPLATE_PREFIX}${contentSetItem.id}` === selectedTemplate)
    const viewLabel = GRAPH_TEMPLATE_LABELS[selectedTemplate] ?? contentSet?.name ?? selectedTemplate
    const sourced = sourcedMetricIdsRef.current
    beginDuplicateBuiltInView({
      viewName: `${viewLabel} - duplicate`,
      sourceViewId: selectedTemplate,
      sourcedMetricIds: sourced?.view === selectedTemplate ? sourced.metricIds : null,
      duplicateFlow,
    })
  }, [selectedTemplate, contentSetsMetadata, duplicateFlow, sourcedMetricIdsRef])

  if (initialSelectedTemplate != null && !isGraphInitialized) {
    return <div className="graph-wrapper" />
  }

  if (isOrphanedCustomSentinel && customTemplatesStatus !== ERROR) {
    return <div className="graph-wrapper" />
  }

  if (isStaleCustomTemplateId) {
    return <div className="graph-wrapper" />
  }

  const isRealCustomTemplateIdAwaitingFeatureResolution = isCustomTemplate
    && selectedTemplate !== "custom"
    && isCustomGraphFeatureUnresolved
  if (isRealCustomTemplateIdAwaitingFeatureResolution) {
    return <div className="graph-wrapper" />
  }

  if (isTemplateListLoadFailure || isSelectedTemplateLoadFailure) {
    return <GraphErrorFallback
      error={new Error(isTemplateListLoadFailure
        ? "Couldn't load your saved views. Please try again."
        : "Couldn't load this saved view. Please try again.")}
      resetError={isTemplateListLoadFailure ? retryTemplatesList : retryTemplateMetadata}
    />
  }

  const template = resolveTemplate(selectedTemplate, isCustomTemplate, isCustomGraphFeatureEnabled)
  const duplicateViewButton = isCustomGraphFeatureEnabled
    && !isCustomTemplate
    && resolveFamilyContextFromBuiltInViewId(selectedTemplate)
    && isCategoryEligibleForDuplicate(selectedTemplate, selectedCategory) ? (
    <DuplicateViewButton onClick={handleDuplicateView} disabled={customTemplatesStatus !== OK} />
    ) : null

  return (
    <div className="graph-wrapper">
      <Paper square className="graph-header" elevation={0}>
        <template.Header
          modelMetadata={modelMetadata}
          sensitivityType={template.sensitivityType}
          templateMetadata={templateMetadata}
          duplicateViewButton={duplicateViewButton}
        />
      </Paper>
      <template.Component
        instrumentsWithMetadata={instrumentsWithMetadata}
        modelMetadata={modelMetadata}
        sensitivityType={template.sensitivityType}
        templateMetadata={templateMetadata}
        templateMetadataStatus={templateMetadataStatus}
        onMetricsChange={handleDisplayedMetricIdsChange}
      />
    </div>
  )
}

GraphWrapper.propTypes = {
  modelMetadata: MODEL_METADATA_SHAPE.isRequired,
  instrumentsWithMetadata: INSTRUMENTS_WITH_METADATA_SHAPE.isRequired,
  initialSelectedTemplate: PropTypes.string,
  initialDateOverride: PropTypes.shape({
    period: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
}
